import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber } from '@/utils/format';
import type { NopLoginDetailDto, NopSnapshotDto, NopSnapshotTotalsDto } from '@/api/types/overnights';

const money = (v: number | null | undefined, d = 2) => (v == null || Number.isNaN(v) ? '' : formatNumber(v, d));

export function BackdatedNopTable({ rows, totals, excelName, card = true }: { rows: NopSnapshotDto[]; totals: NopSnapshotTotalsDto | null; excelName: string; card?: boolean }) {
  const columns: DataGridColumn<NopSnapshotDto>[] = [
    { key: 'symbol', title: 'Instrument', className: 'text-start', value: (r) => r.symbol },
    { key: 'netVolume', title: 'Net Volume', className: 'text-end', value: (r) => r.netVolume, cell: (r) => money(r.netVolume, 3) },
    { key: 'openPrice', title: 'Avg Price', className: 'text-end', value: (r) => r.openPrice, cell: (r) => money(r.openPrice, r.digits || 5) },
    { key: 'floatingPnl', title: 'Floating PnL', className: 'text-end', value: (r) => r.floatingPnl, cell: (r) => <span className={r.floatingPnl < 0 ? 'text-danger' : 'text-success'}>{money(r.floatingPnl, 3)}</span> },
    { key: 'lastPrice', title: 'Last Price', className: 'text-end', value: (r) => r.lastPrice, cell: (r) => money(r.lastPrice, r.digits || 5) },
    { key: 'datasourceName', title: 'Connector', className: 'text-start', value: (r) => r.datasourceName },
    { key: 'concentration', title: 'Concentration %', className: 'text-end', value: (r) => r.concentration, cell: (r) => money(r.concentration, 2) },
  ];

  const totalsRow = totals ? (
    <tr className="fw-semibold">
      <td />
      <td className="text-start">Total</td>
      <td className="text-end">{money(totals.totalNetVolume, 3)}</td>
      <td />
      <td className="text-end">{money(totals.totalFloatingPnl, 3)}</td>
      <td />
      <td />
      <td />
    </tr>
  ) : null;

  return (
    <DataGrid
      columns={columns}
      data={rows}
      rowKey={(r) => `${r.symbol}-${r.dataSourceId}`}
      pageSize={5}
      card={card}
      includeExcel
      excelFileName={excelName}
      footerTop={totalsRow}
      expandable={(r) => <LoginDetails details={r.loginDetails} digits={r.digits || 2} />}
      emptyMessage="No data."
    />
  );
}

function LoginDetails({ details, digits }: { details: NopLoginDetailDto[]; digits: number }) {
  if (!details?.length) return <div className="text-muted small p-2">No login details.</div>;
  return (
    <div className="p-2">
      <table className="table table-sm table-bordered mb-0">
        <thead>
          <tr><th>Login</th><th>Platform</th><th>Unique Id</th><th className="text-end">Net Volume</th><th className="text-end">PnL</th><th className="text-end">Avg Price</th><th className="text-end">Concentration %</th></tr>
        </thead>
        <tbody>
          {details.map((d, i) => (
            <tr key={`${d.login}-${d.platformId}-${i}`}>
              <td>{d.login}</td>
              <td>{d.platform}</td>
              <td>{d.uniqueId}</td>
              <td className="text-end">{money(d.netVolume, 3)}</td>
              <td className={`text-end ${d.pnl < 0 ? 'text-danger' : 'text-success'}`}>{money(d.pnl, digits)}</td>
              <td className="text-end">{money(d.averagePrice, digits)}</td>
              <td className="text-end">{money(d.concentration, 2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
